import React, { useState, useEffect, useRef, useCallback } from "react";
import { useStyles } from "./styles";
import { AxiosResponse } from "axios";
import {
  Typography,
  TextField,
  Button,
  debounce,
  FormControlLabel,
  Checkbox,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import { DeliveryAddress, LookupObject } from "src/types";
import { requestMetaGet, requestPut } from "src/utils/crud";
import SelectInput from "src/components/SelectInput";
import CustomerAutocomplete from "../../../CreateCustomer/CustomerAutocomplete";
import DeliveryAddressIcon from "./icons/DeliveryAddressIcon";
import { useAppContext } from "src/providers/AppProvider";
import { useTranslation } from "react-i18next";
import Modal from "src/components/Modal";
import SuccessNotification from "../SuccessNotification";
import { useCustomerDetailsContext } from "src/providers/CustomerDetailsStore";
import { useParams } from "react-router-dom";
import { CustomerDetailsType } from "../../type";
interface Props {
  data: Partial<DeliveryAddress>;
}

const initialStateDelivery = {
  address: "",
  city: {
    key: undefined,
    displayText: "",
  },
  zipCode: {
    key: undefined,
    displayText: "",
  },
  province: {
    key: undefined,
    displayText: "",
  },
  country: {
    key: undefined,
    displayText: "",
  },
};

const EditDeliveryAddress: React.FC<Props> = ({ data }) => {
  const classes = useStyles();
  const { t } = useTranslation("customers");
  const { customerID } = useParams<CustomerDetailsType>();
  const [deliveryValues, setDeliveryValues] = useState<Partial<DeliveryAddress>>(
    initialStateDelivery
  );
  const [loading, setLoading] = useState<boolean>(false);
  // Handling Delivery Autocomplete
  const [deliveryZipCodesOpen, setDeliveryZipCodesOpen] = useState<boolean>(false);
  const [deliveryZipCodesLoading, setDeliveryZipCodesLoading] = useState<boolean>(false);
  const [deliveryZipCodeValues, setDeliveryZipCodeValues] = useState<LookupObject[]>([]);
  // Set data as Main Address
  const [deliveryAsMain, setDeliveryAsMain] = useState<boolean>(false);
  const [isDefaultAddress, setAsDefaultAddress] = useState<boolean>(false);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const {
    state: {
      modals: { editDeliveryAddressModal },
    },
    callAppAPI: { toggleModal },
  } = useAppContext();
  const {
    selectedCustomer: { customer },
    callAPI: { getDeliveryAddresses },
  } = useCustomerDetailsContext();

  const handleModalClose = useCallback(() => {
    toggleModal({ id: "editDeliveryAddressModal", status: false });
    setSubmitted(false);
  }, []);

  const compareCurrentAndCustomer = () => {
    if (
      deliveryValues.address === customer.address &&
      deliveryValues.country?.key === customer.country?.key &&
      deliveryValues.zipCode?.key === customer.zipCode?.key &&
      deliveryValues.province?.key === customer.province?.key &&
      deliveryValues.city?.key === customer.city?.key
    ) {
      return true;
    } else {
      return false;
    }
  };

  const getZipCodes = debounce(async (displayText: string) => {
    const zipCodes = await requestMetaGet<LookupObject[]>(
      `lookups/zipCodes?countryKey=${deliveryValues.country?.key}&displayText=${displayText}`
    );
    let response = zipCodes.data.map((item) => {
      return { key: item.key, displayText: item.displayText };
    });
    setDeliveryZipCodeValues(response);
    setDeliveryZipCodesLoading(false);
  }, 600);

  const getCascadeData = async (zipCode: string | number) => {
    return await requestMetaGet<Partial<DeliveryAddress>>(
      `lookups/zipCodes/${zipCode}?countryKey=${deliveryValues.country?.key}`
    );
  };

  //Zip Code Autocomplete
  const handleAutocompleteChange = async (value: string, getNewData: boolean) => {
    if (getNewData) {
      setDeliveryZipCodesOpen(true);
      setDeliveryZipCodesLoading(true);
      await getZipCodes(value);
    } else {
      setDeliveryZipCodesOpen(false);
    }
    setDeliveryValues((prev) => ({ ...prev, zipCode: { ...prev.zipCode, displayText: value } }));
    setDeliveryAsMain(false);
  };

  // Zip Code Select
  const handleAutocompleteSelect = (value: number, displayText: string) => {
    setDeliveryValues((prev) => ({ ...prev, zipCode: { ...prev.zipCode, key: value } }));
    setDeliveryZipCodesOpen(false);
    handleDataCascade(displayText);
    setDeliveryAsMain(false);
  };

  const handleDataCascade = async (parentZipCode: string) => {
    const cascadeData = await getCascadeData(parentZipCode);
    if (cascadeData && cascadeData.data) {
      setDeliveryValues({
        ...deliveryValues,
        zipCode: {
          key: cascadeData.data.key,
          displayText: cascadeData.data.displayText,
        },
        city: {
          key: cascadeData.data?.city?.key,
          displayText: cascadeData.data?.city?.displayText,
        },
        province: {
          key: cascadeData.data?.province?.key,
          displayText: cascadeData.data?.province?.displayText,
        },
        country: {
          key: cascadeData.data?.country?.key,
          displayText: cascadeData.data?.country?.displayText,
        },
      });
    }
  };

  // Country Select
  const handleDeliveryDropdownChange = (value: number) => {
    setDeliveryValues({
      ...deliveryValues,
      country: { key: value },
    });
    setDeliveryAsMain(false);
  };

  const handleDeliveryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeliveryValues({ ...deliveryValues, [event.target.name]: event.target.value });
    setDeliveryAsMain(false);
  };

  const handleDeliveryAsMain = () => {
    setDeliveryAsMain(!deliveryAsMain);
    setDeliveryValues({
      ...deliveryValues,
      address: customer.address as string,
      city: { key: customer.city?.key, displayText: customer.city?.displayText },
      zipCode: {
        key: customer.zipCode?.key,
        displayText: customer.zipCode?.displayText,
      },
      province: {
        key: customer.province?.key,
        displayText: customer.province?.displayText,
      },
      country: {
        key: customer.country?.key,
        displayText: customer.country?.displayText,
      },
    });
  };

  const handleMakeAsDefault = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAsDefaultAddress(event.target.checked);
  };

  const submitAddress = async (data: Partial<DeliveryAddress>) => {
    try {
      const res = await requestPut(`DeliveryAddresses/${deliveryValues.key}`, data);
      return res;
    } catch (err) {
      return console.log(err);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    submitButtonRef.current?.setAttribute("disabled", "disabled");

    const deliveryObj = {
      address: deliveryValues.address,
      country: { key: deliveryValues.country?.key },
      city: { key: deliveryValues.city?.key },
      zipCode: { key: deliveryValues.zipCode?.key },
      state: { key: deliveryValues.province?.key },
      isDefault: isDefaultAddress,
      customerId: customerID,
    };
    const addressRes = await submitAddress(deliveryObj);
    if ((addressRes as AxiosResponse).status === 200) {
      setSubmitted(true);
      getDeliveryAddresses(Number(customerID));
    }

    setLoading(false);
    submitButtonRef.current?.removeAttribute("disabled");
  };

  useEffect(() => {
    !deliveryZipCodesOpen && setDeliveryZipCodeValues([]);
  }, [deliveryZipCodesOpen]);

  useEffect(() => {
    if (deliveryAsMain) {
      const matchingValues = compareCurrentAndCustomer();
      !matchingValues && setDeliveryAsMain(false);
    }

    if (
      deliveryValues &&
      deliveryValues.address &&
      deliveryValues.city &&
      deliveryValues.city.key &&
      deliveryValues.country &&
      deliveryValues.country.key
    ) {
      setSubmitDisabled(false);
    } else {
      setSubmitDisabled(true);
    }
  }, [deliveryValues]);

  useEffect(() => {
    if (data) {
      setDeliveryValues({
        key: data.key,
        address: data.address,
        country: data.country,
        zipCode: data.zipCode,
        province: data.state,
        city: data.city,
      });
      setAsDefaultAddress(!!data.isDefault);
    }
  }, [data, editDeliveryAddressModal]);

  return (
    <Modal isOpen={editDeliveryAddressModal} onClose={handleModalClose}>
      <Grid container justify="center">
        <Grid item>
          {!submitted && (
            <form onSubmit={handleSubmit}>
              <Typography variant="h3">{t("labels.details.edit_delivery_address")}</Typography>
              <div className={classes.fieldGroupFirst}>
                <div className={classes.titleWrapper}>
                  <DeliveryAddressIcon />
                  <Typography variant="h4" className={classes.fieldTitle}>
                    {t("labels.create_customer.addresses_form.delivery_address")}
                  </Typography>
                </div>
                <TextField
                  required
                  fullWidth
                  name="address"
                  label={t("labels.create_customer.addresses_form.address")}
                  value={deliveryValues.address}
                  onChange={handleDeliveryChange}
                  variant="outlined"
                  helperText={t("labels.create_customer.addresses_form.address_helper")}
                  FormHelperTextProps={{
                    classes: { root: classes.addressCaption },
                  }}
                />
                <SelectInput
                  required
                  onSelect={handleDeliveryDropdownChange}
                  label={t("labels.create_customer.addresses_form.country")}
                  name="country"
                  value={deliveryValues.country?.key!}
                  url="lookups/countries/delivery"
                  metaApi
                />
                <div className={classes.fieldWrapper}>
                  <CustomerAutocomplete
                    required
                    options={deliveryZipCodeValues}
                    name="zipCode"
                    label={t("labels.create_customer.addresses_form.zip")}
                    inputValue={deliveryValues.zipCode?.displayText}
                    open={deliveryZipCodesOpen}
                    setOpen={setDeliveryZipCodesOpen}
                    loading={deliveryZipCodesLoading}
                    setLoading={setDeliveryZipCodesLoading}
                    onChange={handleAutocompleteChange}
                    onSelect={handleAutocompleteSelect}
                  />
                  <TextField
                    required
                    fullWidth
                    disabled
                    name="cityId"
                    label={t("labels.create_customer.addresses_form.city")}
                    value={deliveryValues.city?.displayText}
                    onChange={handleDeliveryChange}
                    variant="outlined"
                    className={classes.cityField}
                  />
                </div>
                <TextField
                  fullWidth
                  disabled
                  name="province"
                  label={t("labels.create_customer.addresses_form.state")}
                  value={deliveryValues.province?.displayText}
                  onChange={handleDeliveryChange}
                  variant="outlined"
                />
                <FormControlLabel
                  disabled={!customer}
                  control={
                    <Checkbox
                      onClick={handleDeliveryAsMain}
                      checked={deliveryAsMain}
                      color="primary"
                    />
                  }
                  label={t("labels.create_customer.addresses_form.delivery_as_main")}
                />
                <FormControlLabel
                  disabled={!customer}
                  control={
                    <Checkbox
                      onChange={handleMakeAsDefault}
                      checked={isDefaultAddress}
                      color="primary"
                    />
                  }
                  label={"Make as default Delivery address"}
                />
              </div>
              <div className={classes.btnGroup}>
                <Button
                  disabled={submitDisabled}
                  className={classes.submitBtn}
                  type="submit"
                  ref={submitButtonRef}
                >
                  {loading ? (
                    <CircularProgress size={18} color={"secondary"} />
                  ) : (
                    t("labels.common.save")
                  )}
                </Button>
                <Button onClick={handleModalClose} className={classes.cancelBtn}>
                  {t("labels.common.cancel")}
                </Button>
              </div>
            </form>
          )}
          {submitted && (
            <SuccessNotification
              message={t("labels.details.delivery_address_success")}
              onClose={handleModalClose}
            />
          )}
        </Grid>
      </Grid>
    </Modal>
  );
};

export default EditDeliveryAddress;
